// Weiche „Webseite neu“ (Sitzung 9 des Gesamtplans, Daniel 15.10.2026; webseiten-bestandsaufnahme.md 6.1 und 10).
//
// Die öffentlichen Seiten sind live, jeder Push auf `main` geht sofort hinaus. Die neuen Seiten entstehen deshalb
// neben den alten, und diese Weiche wählt je Route neu oder alt. Solange sie aus ist, bleibt die Live-Seite Zeichen
// für Zeichen, wie sie ist (Test `webseiteUnveraendert.test.tsx`), und für Besucher:innen kostet die Weiche keine
// Abfrage: Gefragt wird nur, wenn die Adresse `?webseite=neu` trägt oder dieser Tab sich die Vorschau gemerkt hat.
//
// Vorschau nur für Konten mit Stufe `labs` (`ist_labs_tester()`): Daniel, Nicole, Bianca. Umgeschaltet wird zum
// Relaunch zusammen mit `UMZUG_FUER_ALLE` (src/lib/labs/neueAnsicht.ts), nur auf Daniels ausdrückliches „Start“.
//
// Hier nur Regeln und der Speicher im Browser, ohne Oberfläche, damit sie testbar sind. Der Hook steht in
// `src/components/webseite/useWebseiteNeu.ts`.

import { safeSessionGetItem, safeSessionRemoveItem, safeSessionSetItem } from "@/lib/safeStorage";

/**
 * Steht hier `false`, sehen alle die alte Webseite. Mit `true` sieht jede:r die neue, ohne Abfrage.
 * Umgestellt auf Daniels ausdrückliches „Start“ (Start-Commit, Sitzung SB; ein Test hält den Wert fest). Zurück über
 * einen Commit mit `false` hier und bei `SEO_NEU_FUER_ALLE` in `src/seo/routes.ts`, danach veröffentlichen.
 */
export const WEBSEITE_NEU_FUER_ALLE = true;

/** `?webseite=neu` schaltet die Vorschau in diesem Tab ein, `?webseite=alt` wieder aus. */
export const VORSCHAU_PARAMETER = "webseite";

/** Merkzettel im `sessionStorage`: Die Vorschau gilt für diesen Tab, bis er zu ist oder `?webseite=alt` kommt. */
export const VORSCHAU_SCHLUESSEL = "mk-webseite-vorschau";

/**
 * Hat der Server in diesem Tab schon einmal „Team“ gesagt? Dann bleibt die Vorschau auch nach dem Abmelden, damit das
 * Team Startseite, Anmelden und App-Start so sieht wie Besucher:innen (diese Seiten leiten Angemeldete weiter). Die
 * Seiten zeigen nur Öffentliches; `?webseite=alt` oder ein neuer Tab beendet es.
 */
export const TAB_FREIGABE_SCHLUESSEL = "mk-webseite-vorschau-team";

/** Antwort des Servers je Konto, damit der Tab beim Seitenwechsel nicht erneut fragt. */
export function freigabeSchluessel(userId: string): string {
  return `mk-webseite-vorschau-ok:${userId}`;
}

export type VorschauWunsch = "neu" | "alt" | null;

export function vorschauAusAdresse(suche: string): VorschauWunsch {
  try {
    const wert = new URLSearchParams(suche).get(VORSCHAU_PARAMETER);
    return wert === "neu" || wert === "alt" ? wert : null;
  } catch {
    return null;
  }
}

/** Möchte dieser Tab die Vorschau? Die Adresse geht vor, sonst gilt der Merkzettel. */
export function vorschauGewuenscht(lage: { adresse: VorschauWunsch; gemerkt: boolean }): boolean {
  if (lage.adresse === "neu") return true;
  if (lage.adresse === "alt") return false;
  return lage.gemerkt;
}

/**
 * Zeigt die Seite das Neue? Für alle nach dem Start; vorher nur mit Vorschauwunsch und wenn der Server
 * das Konto als Team (Stufe `labs`) kennt. Solange die Antwort aussteht (`undefined`), bleibt es alt.
 */
export function webseiteNeuAktiv(lage: { fuerAlle: boolean; gewuenscht: boolean; team: boolean | undefined }): boolean {
  if (lage.fuerAlle) return true;
  return lage.gewuenscht && lage.team === true;
}

// ─── Speicher im Browser ──────────────────────────────────────────────────

export function vorschauGemerkt(): boolean {
  return safeSessionGetItem(VORSCHAU_SCHLUESSEL) === "1";
}

/** Merkt sich den Wunsch aus der Adresse für die nächsten Seiten dieses Tabs. */
export function vorschauMerken(wunsch: VorschauWunsch): void {
  if (wunsch === "neu") safeSessionSetItem(VORSCHAU_SCHLUESSEL, "1");
  if (wunsch === "alt") {
    safeSessionRemoveItem(VORSCHAU_SCHLUESSEL);
    safeSessionRemoveItem(TAB_FREIGABE_SCHLUESSEL);
  }
}

export function tabFreigabeGemerkt(): boolean {
  return safeSessionGetItem(TAB_FREIGABE_SCHLUESSEL) === "1";
}

export function freigabeGemerkt(userId: string | null): boolean | undefined {
  if (!userId) return undefined;
  const wert = safeSessionGetItem(freigabeSchluessel(userId));
  return wert === "1" ? true : wert === "0" ? false : undefined;
}

export function freigabeMerken(userId: string, team: boolean): void {
  safeSessionSetItem(freigabeSchluessel(userId), team ? "1" : "0");
  if (team) safeSessionSetItem(TAB_FREIGABE_SCHLUESSEL, "1");
}
